import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { locales } from '@alexis/helpers/site';
import { translateText } from '@alexis/helpers/translation';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import ProfileSetupProgress from '@components/ProfileSetupProgress/ProfileSetupProgress';

import { GlobalContext } from '@context/GlobalContext';

import { isWegoShopcashEnabled } from '@helpers/shopcash';

import BusinessOutlineIcon from '@icons/businessOutline.svg';
import ChevronRightSmallIcon from '@icons/chevron-right-small.svg';
import DashboardIcon from '@icons/dashboard.svg';
import HelpIcon from '@icons/help.svg';
import PeopleIcon from '@icons/people.svg';
import ProfileIcon from '@icons/profile.svg';
import SettingsIcon from '@icons/settings.svg';

import {
  getCurrentSiteState,
  getLocaleState,
  getShopCashUserState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import UserSummaryInfo from '../UserSummaryInfo';
import PreferencesSideNavigationLink from './PreferenceSideNavigationLink';
import styles from './PreferencesSideNavigation.module.scss';
import PreferencesSideNavigationAccordion from './PreferencesSideNavigationAccordion';

type SidebarNavItem = {
  labelKey: string;
  icon?: React.FC<React.SVGAttributes<SVGElement>>;
  href?: string;
  children?: SidebarNavItem[];
  tracking?: {
    category: string;
    object: string;
    action: string;
    value: string;
  };
};

const SIDEBAR_NAV_ITEMS: SidebarNavItem[] = [
  {
    labelKey: 'dashboard',
    icon: DashboardIcon,
    href: '/preferences',
  },
  {
    labelKey: 'my_profile',
    icon: ProfileIcon,
    children: [
      {
        labelKey: 'my_personal_info',
        href: '/preferences/profile/personal-info',
        tracking: {
          category: 'personal_info',
          object: 'form',
          action: 'clicked',
          value: '',
        },
      },
      {
        labelKey: 'my_wallet',
        href: '/preferences/profile/wallet',
      },
      {
        labelKey: 'preferred_payment_method',
        href: '/preferences/profile/payment-methods',
      },
    ],
  },
  {
    labelKey: 'add_edit_traveller',
    icon: () => <PeopleIcon width='24px' />,
    href: '/preferences/profile/travellers',
    tracking: {
      category: 'traveler_form',
      object: 'traveler_form',
      action: 'clicked',
      value: '',
    },
  },
  {
    labelKey: 'my_trips',
    icon: BusinessOutlineIcon,
    children: [
      {
        labelKey: 'hotel_bookings',
        href: '/preferences/trips/hotels',
      },
      {
        labelKey: 'flight_bookings',
        href: '/preferences/trips/flights',
      },
      // {
      //   labelKey: 'add_edit_traveler',
      //   href: '/preferences/trips/traveler',
      // },
    ],
  },
  {
    labelKey: 'settings',
    icon: SettingsIcon,
    children: [
      // {
      //   labelKey: 'change_password',
      //   href: '/preferences/settings/password',
      // },
      // {
      //   labelKey: 'link_account',
      //   href: '/preferences/settings/link-account',
      // },
      // {
      //   labelKey: 'notifications',
      //   href: '/preferences/settings/notifications',
      // },
      {
        labelKey: 'terms_of_use',
        href: 'https://company.wego.com/terms-of-service',
      },
      {
        labelKey: 'privacy_policy',
        href: 'https://company.wego.com/data-privacy-policy',
      },
    ],
  },
  {
    labelKey: 'help_center',
    icon: HelpIcon,
    children: [
      // {
      //   labelKey: 'faqs',
      //   href: '/preferences/help/faqs',
      // },
      {
        labelKey: 'contact_us',
        href: '/preferences/help/contact-us',
      },
    ],
  },
];

type PreferencesSideNavigationProps = {
  className?: string;
  localeParam?: string;
  pageViewId: string;
};

const PreferencesSideNavigation: React.FC<PreferencesSideNavigationProps> = ({
  className,
  localeParam,
  pageViewId,
}: PreferencesSideNavigationProps) => {
  const localeCodes = locales.map(({ code }) => code);
  const localeRegex = new RegExp(`^/(${localeCodes.join('|')})`);

  const { userCentricCoreConfigs } = useContext(GlobalContext);

  const personalInfoConfig = userCentricCoreConfigs?.find(
    (coreConfig) => coreConfig.key === 'dw_personal_info_enable',
  );

  const isMyPersonalInfoEnabled = personalInfoConfig?.value === '1';

  const { pathname } = useLocation();

  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const currentSite = useSelector(getCurrentSiteState);
  const shopCashUser = useSelector(getShopCashUserState);
  const shouldShowWallet = isWegoShopcashEnabled(currentSite.countryCode) && !!shopCashUser;

  const user = useSelector(getUserState);

  const sidebarNavItems = SIDEBAR_NAV_ITEMS.filter((item) => {
    if (item.labelKey === 'add_edit_traveller' && !isMyPersonalInfoEnabled) {
      return false;
    }
    return true;
  }).map((item) => {
    if (item.labelKey === 'my_profile') {
      let children = item.children;
      if (!shouldShowWallet) {
        children = children?.filter((child) => child.labelKey !== 'my_wallet');
      }
      if (!isMyPersonalInfoEnabled) {
        children = children?.filter((child) => child.labelKey !== 'my_personal_info');
      }
      return {
        ...item,
        children,
      };
    }

    if (item.labelKey === 'settings')
      return {
        ...item,
        children: [
          ...(item.children || []),
          {
            labelKey: 'delete_account',
            href: pathname.replace(localeRegex, '') + `?modal=delete-account`,
          },
        ],
      };

    return item;
  });

  const isNavActive = (href: string) => {
    return pathname.replace(localeRegex, '') === href;
  };

  const handleNavItemClick = (item: SidebarNavItem) => {
    if (item.tracking) {
      const wegoAnalyticsClientId = getCookie('wego_analytics_client_id');
      const wegoAnalyticsClientSessionId = getCookie('wego_analytics_client_session_id');
      if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId!,
          wegoAnalyticsClientSessionId!,
          user?.userHash,
          {
            id: pageViewId,
            category: item.tracking.category,
            object: item.tracking.object,
            action: item.tracking.action,
            value: item.tracking.value,
          },
        );
      }
    }
  };

  return (
    <div className={clsx(styles.container, className)}>
      <UserSummaryInfo />
      {isMyPersonalInfoEnabled && <ProfileSetupProgress />}
      <ul className={styles.navItems}>
        {sidebarNavItems.map((item) => {
          if (item.children?.length)
            return (
              <li key={item.labelKey}>
                <PreferencesSideNavigationAccordion
                  className={styles.navItem}
                  defaultOpen={!!item.children.find((subItem) => isNavActive(subItem.href!))}
                >
                  {!!item.icon && <item.icon />}
                  <span>
                    {translations[item.labelKey]
                      ? translateText(translations[item.labelKey], locale)
                      : item.labelKey}
                  </span>
                </PreferencesSideNavigationAccordion>
                <ul className={styles.navSubItems}>
                  {item.children.map((child) => (
                    <li key={child.labelKey}>
                      <PreferencesSideNavigationLink
                        localeParam={localeParam}
                        to={child.href!}
                        className={styles.navSubItem}
                        data-active={isNavActive(child.href!)}
                        onClick={() => handleNavItemClick(child)}
                      >
                        <span>
                          {translations[child.labelKey]
                            ? translateText(translations[child.labelKey], locale)
                            : child.labelKey}
                        </span>
                        <ChevronRightSmallIcon className={styles.icon} />
                      </PreferencesSideNavigationLink>
                    </li>
                  ))}
                </ul>
              </li>
            );

          if (item.href)
            return (
              <li key={item.labelKey}>
                <PreferencesSideNavigationLink
                  localeParam={localeParam}
                  to={item.href}
                  className={styles.navItem}
                  data-active={isNavActive(item.href!)}
                  onClick={() => handleNavItemClick(item)}
                >
                  {!!item.icon && <item.icon />}
                  <span>{translateText(translations[item.labelKey], locale)}</span>
                  <ChevronRightSmallIcon className={styles.icon} />
                </PreferencesSideNavigationLink>
              </li>
            );
        })}
        {/*   <li>
          <button type='button' className={styles.navItem} onClick={handleLogOut}>
            <LogoutIcon />
            <span>{translateText(translations.logout, locale)}</span>
          </button>
        </li> */}
      </ul>
    </div>
  );
};

export default PreferencesSideNavigation;
